import {
  BookingActionButtonColor,
  Hyperlink,
  SpecialPage as RawSpecialPage,
  SpecialPageModule,
} from '../server-middleware/data-server/adapter/ACCOAdapter/@types/index';
import { ResolvedLanugageData } from '../server-middleware/data-server/adapter/ACCOAdapter/schemes/language';
import { AvailableLanguage, LanguageDataMap } from './language';
import { SearchTagCategoryCommons } from '.';

export {
  SpecialPageSpace,
  SpecialPageModule,
  CustomHTMLModule,
  KeyVisualModule,
  ResponsiveBreakpoint,
  TextOrImageAsTextWithOption,
  HeadingModule,
  LeadTextModule,
  HotelIntroductionListModule,
  HotelIntroductionModule,
  AttractiveIntroductionModule,
  AttractiveIntroductionListModule,
  HotelWithAttractionsModule,
  HotelWithAttractionsListModule,
  PartitionModule,
  MediaModule,
  MediaModuleAspectRatio,
  MediaModuleDerivedSize,
  MediaModuleSize,
  MediaModuleType,
  MediaModuleDerivedName,
  SimpleTableModule,
  SimpleTableRow,
  TextModule,
  EmbedModule,
  EmbedFormModule,
  NavigationModule,
  LinkButtonModule,
  LinkButtonListModule,
  ProfileModule,
  BannerModule,
  EmbedMapModule,
  RichTableModule,
  AccordionModule,
  AccordionListModule,
  RecommendedItemsModule,
  ArticleContentListModule,
  ArticleTemplateModule,
  StoryModule,
  ArticleListModule,
  ArticleListItemModule,
  AlternateLayoutModule,
  AlternateLayoutItemModule,
  YouTubeModule,
} from '../server-middleware/data-server/adapter/ACCOAdapter/@types/index';

/** 特集ページの親ページのサマリ */
export interface SpecialPageParentSummery {
  /** URLスラッグ */
  slug: string;
  /** ページタイトル */
  title: string;
}

/**
 * 特集ページのGF表示で必要なプロパティのみを取得した型
 */
export type SpecialPage = Omit<
  ResolvedLanugageData<RawSpecialPage, AvailableLanguage>,
  | 'createdAt'
  | 'adminName'
  | 'publishedFrom'
  | 'publishedUntil'
  | 'publishedAt'
  | 'published'
> &
  Partial<SearchTagCategoryCommons> & {
    parents: SpecialPageParentSummery[];
  };

export interface SpecialPagePriority extends SpecialPage {
  /** 遷移先URL */
  link: string;
}

export interface SearchSpCondition {
  page?: number;
  brandSlug?: string;
}
export interface SpecialPageListMetaInfoLS {
  title: LanguageDataMap<string>;
  siteName: LanguageDataMap<string>;
  description: LanguageDataMap<string>;
}

/** 文字色 */
const BOOKING_ACTION_BUTTON_LETTER_COLORS = ['#FFF', '#1A1A1A'] as const;

export type BookingActionButtonLetterColor = typeof BOOKING_ACTION_BUTTON_LETTER_COLORS[number];

interface BookingActionButtonSettings {
  /**
   * 文字色
   * @see {@link BookingActionButtonLetterColor}
   */
  color: BookingActionButtonLetterColor;
  /**
   * 背景色
   * @see {@link BookingActionButtonBgColor}
   */
  bgColor: BookingActionButtonBgColor;
}
/**
 * 空室検索ボタン設定
 */
export interface BookingActionSetting {
  /**
   * ボタンカラー
   */
  color?: BookingActionButtonColor;

  /**
   * 遷移先URL/文言
   */
  link?: Hyperlink;
}

/** 背景色 */
const BOOKING_ACTION_BUTTON_BG_COLORS = [
  '#E6E6E6',
  '#4D4D4D',
  '#00A0CB',
] as const;

export type BookingActionButtonBgColor = typeof BOOKING_ACTION_BUTTON_BG_COLORS[number];

/** 空室検索ボタンカラーに紐づく文字色と背景色のマッピング */
export const BOOKING_ACTION_BUTTON_SETTINGS_MAP: Record<
  BookingActionButtonColor,
  BookingActionButtonSettings
> = {
  default: {
    color: '#1A1A1A',
    bgColor: '#E6E6E6',
  },
  emphasis: {
    color: '#FFF',
    bgColor: '#4D4D4D',
  },
  action: {
    color: '#FFF',
    bgColor: '#00A0CB',
  },
};

/** 内部リンク接頭語 */
export const NAVIGATION_PREFIX = 'sp-';

/** 特集ページの階層構造内区切り文字 */
export const SPECIAL_PAGE_PATH_DELIMITER = '@';

/** 検索可能なモジュールIDリスト */
export const SEARCHABLE_SPECIAL_PAGE_MODULE_IDS = [
  'AttractiveIntroductionList',
  'HotelIntroductionList',
  'ArticleList',
] as const;
export type SearchableSpecialPageModuleId = typeof SEARCHABLE_SPECIAL_PAGE_MODULE_IDS[number];

export type SpecialPageModuleId = SpecialPageModule['moduleId'];

export type SpecialPageModuleById<
  Id extends SpecialPageModuleId
> = ResolvedLanugageData<Extract<SpecialPageModule, { moduleId: Id }>, 'ja'>;

export type SearchableSpecialPageModule<
  Id extends SearchableSpecialPageModuleId = SearchableSpecialPageModuleId
> = SpecialPageModuleById<Id>;

export type SearchableSpecialPageModuleProperties = SearchableSpecialPageModule['properties'];

export function isSearchableSpecialPageModule(
  specialPageModule: ResolvedLanugageData<SpecialPageModule, 'ja'>,
): specialPageModule is SearchableSpecialPageModule {
  return SEARCHABLE_SPECIAL_PAGE_MODULE_IDS.includes(
    specialPageModule.moduleId as SearchableSpecialPageModuleId,
  );
}
